/**
 * Super Admin — past sync runs.
 *
 * `sync_jobs` has recorded every run since 2026-07-21 and nothing prunes it, yet
 * until this page the only way to read it was psql: `/settings` shows one
 * timestamp per account, `/admin/sync-schedules` shows one per workspace, and
 * neither can answer "what happened on the 6th". Every sync incident this month
 * — 17 jobs killed by Facebook's error 17, a worker that had never run on UAT, a
 * backfill that skipped 201 of 222 windows — was diagnosed from this table by
 * hand.
 *
 * THE ROLLUP COMES FIRST, and that ordering is the design. A flat list does not
 * survive real volume: the measured worst day was 305 jobs, of which 260 were
 * probe skips, so the one `failed` that mattered sat 200 rows below the fold. The
 * daily table answers the shape of the question ("17 failed on the 6th, none
 * since") and the detail list is for after you know which day to open.
 *
 * SKIPS ARE HIDDEN BY DEFAULT for the same reason — 209 of 231 rows on 10-07 were
 * `no_data`, each one a probe correctly declining to walk an account. They are
 * the bulk of the table and almost never the thing being looked for, so they are
 * one checkbox away rather than in the way. The count is always stated, because a
 * filtered table that does not say what it hid is a table that lies.
 *
 * Reads two SECURITY DEFINER RPCs that check is_platform_master() themselves, so
 * there is no endpoint in front of this — the database is the boundary.
 *
 * @module pages/admin/SyncHistory
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ShieldAlert, RefreshCw, AlertTriangle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { YDMPageHero } from "../../components/brand";
import {
  listSyncHistory,
  listSyncHistoryDaily,
  type SyncHistoryRow,
  type SyncHistoryDay,
} from "../../services/syncHistoryService";

const RANGES = [7, 30, 90] as const;
/** One bad backfill day is 305 rows; this holds a whole incident without holding the table hostage. */
const ROW_LIMIT = 500;

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

/**
 * A scheduled sync carries no window, and that absence is information: it means
 * "the usual 7 days ending now", not "unknown". (The stored source for those is
 * `schedule`; `routine` is UI vocabulary that never reaches the column.)
 */
const formatWindow = (from: string | null, to: string | null): string =>
  from && to ? `${from} → ${to}` : "7 วันปกติ";

const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return "—";
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

/** Grey for a skip, red only for a real failure — a skip is a correct outcome. */
const STATUS_STYLE: Record<string, string> = {
  success: "bg-green-100 text-green-900",
  failed: "bg-red-100 text-red-900",
  no_data: "bg-neutral-100 text-neutral-700",
  partial: "bg-yellow-100 text-yellow-900",
  running: "bg-blue-100 text-blue-900",
  queued: "bg-blue-50 text-blue-900",
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
      STATUS_STYLE[status] ?? "bg-neutral-100 text-neutral-700"
    }`}
  >
    {status}
  </span>
);

const Tile: React.FC<{ label: string; value: string; alert?: boolean }> = ({ label, value, alert }) => (
  <div className="bg-white rounded-lg border border-neutral-200 px-4 py-3">
    <p className="text-xs text-neutral-700">{label}</p>
    <p className={`text-lg font-semibold ${alert ? "text-red-800" : "text-ink-900"}`}>{value}</p>
  </div>
);

const AdminSyncHistory: React.FC = () => {
  const [rows, setRows] = useState<SyncHistoryRow[]>([]);
  const [days_, setDays] = useState<SyncHistoryDay[]>([]);
  const [range, setRange] = useState<number>(7);
  const [showSkips, setShowSkips] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // The rollup window is deliberately wider than the detail window: the point
    // of the summary is to spot the day worth opening, and a 7-day detail view
    // beside a 7-day summary can only ever confirm what is already on screen.
    const [detail, daily] = await Promise.all([
      listSyncHistory(range, ROW_LIMIT),
      listSyncHistoryDaily(Math.max(range, 30)),
    ]);

    if (detail.notAuthorized || daily.notAuthorized) {
      // 42501 from either guard is the same fact, and it is NOT a load failure.
      setDenied(true);
      setError(detail.error ?? daily.error ?? null);
    } else if (detail.success && daily.success) {
      setRows(detail.data ?? []);
      setDays(daily.data ?? []);
      setDenied(false);
      setError(null);
    } else {
      // Both read the same table through the same gate, so one failing alone is
      // unlikely — and half a picture presented as the whole one is worse than none.
      setDenied(false);
      setError(detail.error ?? daily.error ?? "โหลดประวัติการ sync ไม่สำเร็จ");
    }
    setLoading(false);
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  const skipped = useMemo(() => rows.filter((r) => r.status === "no_data").length, [rows]);
  const visible = useMemo(
    () => (showSkips ? rows : rows.filter((r) => r.status !== "no_data")),
    [rows, showSkips],
  );

  const totals = useMemo(
    () => ({
      jobs: rows.length,
      failed: rows.filter((r) => r.status === "failed").length,
      // Summed off the detail rows, so this is "within the rows shown" and not a
      // second, differently-scoped total that would quietly disagree with the table.
      metrics: rows.reduce((sum, r) => sum + r.metricsWritten, 0),
    }),
    [rows],
  );

  const hero = (
    <YDMPageHero
      eyebrow="Super Admin"
      headline="SYNC HISTORY"
      subtitle="ประวัติการ sync ย้อนหลัง ทุก workspace"
      actions={
        <div className="flex items-center gap-2">
          {/* This page is reached from Sync schedules and is deliberately NOT in the
              admin menu, so without this link a visitor's only way back is the
              browser button — and the master fallback below renders this same hero,
              where that would be the only way out at all. */}
          <Link
            to="/admin/sync-schedules"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
          >
            <ArrowLeft size={13} />
            Sync schedules
          </Link>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-ink-950 text-white hover:bg-ink-900 disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : undefined} />
            Refresh
          </button>
        </div>
      }
    />
  );

  if (denied) {
    return (
      <div className="space-y-4 animate-fade-in">
        {hero}
        <div className="bg-white rounded-lg border border-neutral-200 p-8 text-center">
          <ShieldAlert className="w-12 h-12 text-yellow-700 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-ink-900 mb-2">This area is for the platform master</h2>
          <p className="text-neutral-700">{error}</p>
        </div>
      </div>
    );
  }

  // Kept apart from the panel above: a dropped connection is not a permission
  // problem, and telling an operator they lack access when the database merely
  // blinked sends them to the wrong person.
  if (error) {
    return (
      <div className="space-y-4 animate-fade-in">
        {hero}
        <div className="bg-white rounded-lg border border-neutral-200 p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-700 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-ink-900 mb-2">โหลดประวัติไม่สำเร็จ</h2>
          <p className="text-neutral-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {hero}

      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setRange(d)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
              range === d
                ? "bg-ink-950 text-white border-ink-950"
                : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"
            }`}
          >
            {d} วัน
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white rounded-lg border border-neutral-200 p-12 text-center">
          <Loader2 className="w-8 h-8 text-neutral-700 mx-auto animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Tile label={`งานในช่วง ${range} วัน`} value={totals.jobs.toLocaleString()} />
            <Tile label="ล้มเหลว" value={totals.failed.toLocaleString()} alert={totals.failed > 0} />
            <Tile label="metric ที่เขียน" value={totals.metrics.toLocaleString()} />
          </div>

          <section className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
            <header className="px-4 py-3 border-b border-neutral-200">
              <h2 className="text-sm font-semibold text-ink-900">สรุปรายวัน</h2>
              <p className="text-xs text-neutral-700">
                ย้อนหลัง {Math.max(range, 30)} วัน — ใช้หาว่าวันไหนควรเปิดดูรายละเอียด
              </p>
            </header>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 text-xs text-neutral-700">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">วัน</th>
                    <th className="px-4 py-2 text-left font-medium">ที่มา</th>
                    <th className="px-4 py-2 text-right font-medium">งาน</th>
                    <th className="px-4 py-2 text-right font-medium">สำเร็จ</th>
                    <th className="px-4 py-2 text-right font-medium">ข้าม</th>
                    <th className="px-4 py-2 text-right font-medium">ล้มเหลว</th>
                    <th className="px-4 py-2 text-right font-medium">ค้าง</th>
                    <th className="px-4 py-2 text-right font-medium">metric</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {days_.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-center text-neutral-700">
                        ยังไม่มีการ sync ในช่วงนี้
                      </td>
                    </tr>
                  ) : (
                    days_.map((d) => (
                      <tr key={`${d.day}-${d.source}`}>
                        <td className="px-4 py-2 text-ink-900">{d.day}</td>
                        <td className="px-4 py-2 text-neutral-700">{d.source}</td>
                        <td className="px-4 py-2 text-right text-ink-900">{d.jobs.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-neutral-700">{d.succeeded.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-neutral-700">{d.noData.toLocaleString()}</td>
                        <td
                          className={`px-4 py-2 text-right ${
                            d.failed > 0 ? "font-semibold text-red-800" : "text-neutral-700"
                          }`}
                        >
                          {d.failed.toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-right text-neutral-700">{d.active.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-neutral-700">{d.metricsWritten.toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
            <header className="px-4 py-3 border-b border-neutral-200 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold text-ink-900">รายการงาน</h2>
                <p className="text-xs text-neutral-700">
                  {visible.length.toLocaleString()} จาก {rows.length.toLocaleString()} งาน
                  {rows.length >= ROW_LIMIT ? ` (ตัดที่ ${ROW_LIMIT} แถว)` : ""}
                  {skipped > 0 && !showSkips ? ` · ซ่อนงานที่ probe ข้ามไว้ ${skipped.toLocaleString()} งาน` : ""}
                </p>
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-neutral-700">
                <input
                  type="checkbox"
                  checked={showSkips}
                  onChange={(e) => setShowSkips(e.target.checked)}
                  className="rounded border-neutral-300"
                />
                แสดงงานที่ถูกข้าม
              </label>
            </header>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 text-xs text-neutral-700">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">เมื่อ</th>
                    <th className="px-3 py-2 text-left font-medium">workspace</th>
                    <th className="px-3 py-2 text-left font-medium">บัญชี</th>
                    <th className="px-3 py-2 text-left font-medium">ที่มา</th>
                    <th className="px-3 py-2 text-left font-medium">สถานะ</th>
                    <th className="px-3 py-2 text-left font-medium">ช่วงที่ขอ</th>
                    <th className="px-3 py-2 text-right font-medium">entities</th>
                    <th className="px-3 py-2 text-right font-medium">metric</th>
                    <th className="px-3 py-2 text-right font-medium">ใช้เวลา</th>
                    <th className="px-3 py-2 text-left font-medium">สั่งโดย</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-3 py-6 text-center text-neutral-700">
                        {rows.length > 0
                          ? "ทุกงานในช่วงนี้ถูก probe ข้าม — ติ๊ก “แสดงงานที่ถูกข้าม” เพื่อดู"
                          : "ยังไม่มีการ sync ในช่วงนี้"}
                      </td>
                    </tr>
                  ) : (
                    visible.map((r) => (
                      <tr key={r.id} className="align-top">
                        <td className="px-3 py-2 whitespace-nowrap text-ink-900">{formatWhen(r.createdAt)}</td>
                        <td className="px-3 py-2 text-neutral-700">{r.workspaceLabel}</td>
                        <td className="px-3 py-2 text-neutral-700">
                          {r.accountLabel}
                          <span className="block text-xs text-neutral-700">{r.platform}</span>
                        </td>
                        <td className="px-3 py-2 text-neutral-700">{r.source}</td>
                        <td className="px-3 py-2">
                          <StatusPill status={r.status} />
                          {r.errorMessage && (
                            <span className="block mt-1 max-w-xs text-xs text-neutral-700">{r.errorMessage}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-neutral-700">
                          {formatWindow(r.startDate, r.endDate)}
                        </td>
                        <td className="px-3 py-2 text-right text-neutral-700">
                          {r.entitiesProcessed.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right text-neutral-700">{r.metricsWritten.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-neutral-700">
                          {formatDuration(r.durationSeconds)}
                        </td>
                        <td className="px-3 py-2 text-neutral-700">{r.requestedByLabel}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminSyncHistory;
